import Button from "@mui/material/Button";
import { FormDialog } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps
} from "components/common/FormDialog/types";
import { Table } from "components/common/Table";
import {
  TABLE_SORTING_TYPES,
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { objectStorageActions } from "modules/objectStorage/slices";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { notificationsActions } from "modules/notifications/slices";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useParams } from "react-router-dom";
import { ERROR_MESSAGES, REGEX, ROUTES } from "../../constants";
import { DIALOG_TYPES, S3BucketsProps } from "./types";
import { TableS3Buckets } from "modules/objectStorage/types";
import {
  isBucketCreatingSelector,
  isBucketDeletingSelector,
  isBucketVisibilityChangingSelector,
  isVersioningStatusUpdatingSelector,
  tableBucketsListSelector
} from "modules/objectStorage/selectors";
import { string } from "yup";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import copy from "copy-to-clipboard";

const POLL_ID_PREFIX = "S3_BUCKETS";

const POLL_IDS = {
  credentialsList: "CREDENTIALS_LIST",
  bucketsList: "BUCKETS_LIST"
};

const tableColumns: TableColumn<TableS3Buckets>[] = [
  { key: "name", label: "Name" },
  { key: "visibility", label: "Visibility" },
  { key: "is_versioning", label: "Versioning" },
  {
    key: "creationDate",
    label: "Created",
    sortingType: TABLE_SORTING_TYPES.DATE
  }
];

export const S3Buckets: FC<S3BucketsProps> = ({ credsId, tabs }) => {
  const dispatch = useDispatch();

  const matchParams = useParams<{
    organizationId: string;
    regionId: string;
    projectId: string;
  }>();

  const bucketsList = useSelector(tableBucketsListSelector);

  const isBucketCreating = useSelector(isBucketCreatingSelector);
  const isBucketDeleting = useSelector(isBucketDeletingSelector);
  const isBucketVisibilityChanging = useSelector(
    isBucketVisibilityChangingSelector
  );
  const isVersioningStatusUpdating = useSelector(
    isVersioningStatusUpdatingSelector
  );
  const isBucketOperationInProgress =
    isBucketCreating ||
    isBucketDeleting ||
    isBucketVisibilityChanging ||
    isVersioningStatusUpdating;
  const previousIsBucketOperationInProgress = usePrevious(
    isBucketOperationInProgress
  );

  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.DELETE, isOpened: false });

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // to add name to the delete confirmation window
  const previousSelectedItemId = usePrevious(selectedItemId);
  const selectedItemName = selectedItemId
    ? selectedItemId
    : previousSelectedItemId;
  const selectedItemURL = bucketsList?.find(
    (bucket) => bucket.name === selectedItemName
  )?.url;

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
    setSelectedItemId(null);
  }, [dialog]);

  const generateTableItemURL = useCallback(
    (name: string) =>
      generatePath(ROUTES.S3_BUCKET, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId,
        credsId,
        bucketName: name
      }),
    [
      matchParams.organizationId,
      matchParams.regionId,
      matchParams.projectId,
      credsId
    ]
  );

  const handleCreateBucketButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.CREATE,
      isOpened: true
    });
  }, []);

  const handleMakePrivateBucketMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.MAKE_PRIVATE,
      isOpened: true
    });
  }, []);

  const handleMakePublicBucketMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.MAKE_PUBLIC,
      isOpened: true
    });
  }, []);

  const handleShowPublicUrlMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.SHOW_PUBLIC_URL,
      isOpened: true
    });
  }, []);

  const handleDeleteBucketMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE,
      isOpened: true
    });
  }, []);

  const handleEnableBucketVersioningMenuItemClick = useCallback(
    (id: string) => {
      setSelectedItemId(id);
      setDialog({
        type: DIALOG_TYPES.ENABLE_VERSIONING,
        isOpened: true
      });
    },
    []
  );

  const handleSuspendBucketVersioningMenuItemClick = useCallback(
    (id: string) => {
      setSelectedItemId(id);
      setDialog({
        type: DIALOG_TYPES.SUSPEND_VERSIONING,
        isOpened: true
      });
    },
    []
  );

  const tableActions: TableRowActionsMenuItem<TableS3Buckets>[] = [
    {
      label: "Make private",
      handler: handleMakePrivateBucketMenuItemClick,
      isDisabled: (bucket) => bucket.visibility !== "public"
    },
    {
      label: "Make public",
      handler: handleMakePublicBucketMenuItemClick,
      isDisabled: (bucket) => bucket.visibility === "public"
    },
    {
      label: "Show URL",
      handler: handleShowPublicUrlMenuItemClick,
      isDisabled: (bucket) => bucket.visibility !== "public"
    },
    {
      label: "Enable versioning",
      handler: handleEnableBucketVersioningMenuItemClick,
      isDisabled: (bucket) => bucket.is_versioning === "enabled"
    },
    {
      label: "Suspend versioning",
      handler: handleSuspendBucketVersioningMenuItemClick,
      isDisabled: (bucket) => bucket.is_versioning !== "enabled"
    },
    {
      label: "Delete bucket",
      handler: handleDeleteBucketMenuItemClick
    }
  ];

  useMount(() => {
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.bucketsList}`,
        actionCreator: objectStorageActions.getBucketsRequest,
        params: {
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId
        }
      })
    );
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
  });

  useEffect(() => {
    if (previousIsBucketOperationInProgress && !isBucketOperationInProgress) {
      dispatch(
        objectStorageActions.getBucketsRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId
        })
      );
    }
  }, [
    dispatch,
    matchParams.projectId,
    matchParams.regionId,
    credsId,
    previousIsBucketOperationInProgress,
    isBucketOperationInProgress
  ]);

  const handleConfirmCreateBucket = useCallback(
    (data: { name: string }) => {
      dispatch(
        objectStorageActions.createBucketRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId,
          data
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      handleCloseDialog,
      credsId,
      matchParams.projectId,
      matchParams.regionId
    ]
  );

  const handleConfirmChangeBucketVisibility = useCallback(
    (visibility) => {
      if (selectedItemId) {
        dispatch(
          objectStorageActions.changeBucketVisibilityRequest({
            regionId: matchParams.regionId!,
            projId: matchParams.projectId!,
            credsId,
            bucketName: selectedItemId,
            visibility
          })
        );
      }
      handleCloseDialog();
    },
    [
      selectedItemId,
      handleCloseDialog,
      dispatch,
      matchParams.regionId,
      matchParams.projectId,
      credsId
    ]
  );

  const handleConfirmUpdateVersioningStatus = useCallback(
    (versioningStatus) => {
      if (selectedItemId) {
        dispatch(
          objectStorageActions.updateVersioningStatusRequest({
            regionId: matchParams.regionId!,
            projId: matchParams.projectId!,
            credsId,
            bucketName: selectedItemId,
            versioningStatus
          })
        );
      }
      handleCloseDialog();
    },
    [
      selectedItemId,
      handleCloseDialog,
      dispatch,
      matchParams.regionId,
      matchParams.projectId,
      credsId
    ]
  );

  const handleConfirmDeleteBucket = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        objectStorageActions.deleteBucketRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId,
          name: selectedItemId
        })
      );
    }
    handleCloseDialog();
  }, [
    selectedItemId,
    handleCloseDialog,
    dispatch,
    matchParams.regionId,
    matchParams.projectId,
    credsId
  ]);

  const handleCopyBucketUrl = useCallback(() => {
    if (selectedItemURL) {
      copy(selectedItemURL);
      dispatch(
        notificationsActions.showNotification({
          type: NOTIFICATION_TYPES.INFO,
          title: "Bucket URL has been copied to clipboard."
        })
      );
    }
  }, [dispatch, selectedItemURL]);

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.CREATE]: {
      onConfirm: handleConfirmCreateBucket,
      title: "Create bucket",
      confirmButtonLabel: "Create",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          rules: string()
            .required()
            .matches(REGEX.BUCKET_NAME, ERROR_MESSAGES.BUCKET_NAME)
            .min(3, ERROR_MESSAGES.MIN_BUCKET_NAME)
            .max(63, ERROR_MESSAGES.MAX_BUCKET_NAME)
            .test(
              "sameNameCheckFormat",
              "This name already exists",
              (value) => {
                const name = bucketsList?.find(
                  (bucket) => bucket.id === value
                )?.name;
                return Boolean(value && !name);
              }
            )
        }
      ]
    },
    [DIALOG_TYPES.MAKE_PRIVATE]: {
      onConfirm: () => {
        handleConfirmChangeBucketVisibility("private");
      },
      title: `Are you sure you want to make bucket "${
        selectedItemName ?? "selected"
      }" private?`,
      fields: [
        {
          name: "bucketPolicyWarning",
          type: FIELD_TYPES.NOTES,
          label: "⚠️ The current bucket policy will be completely overwritten."
        }
      ],
      confirmButtonLabel: "Make Private"
    },
    [DIALOG_TYPES.MAKE_PUBLIC]: {
      onConfirm: () => {
        handleConfirmChangeBucketVisibility("public");
      },
      title: `Are you sure you want to make bucket "${
        selectedItemName ?? "selected"
      }" public?`,
      fields: [
        {
          name: "bucketPolicyWarning",
          type: FIELD_TYPES.NOTES,
          label: "⚠️ The current bucket policy will be completely overwritten."
        }
      ],
      confirmButtonLabel: "Make Public"
    },
    [DIALOG_TYPES.SHOW_PUBLIC_URL]: {
      onConfirm: handleCopyBucketUrl,
      title: `Bucket URL`,
      fields: [
        {
          name: "url",
          type: FIELD_TYPES.LABEL,
          label: "Bucket url",
          link: `${selectedItemURL ?? ""}`
        }
      ],
      confirmButtonLabel: "Copy to clipboard"
    },
    [DIALOG_TYPES.ENABLE_VERSIONING]: {
      onConfirm: () => {
        handleConfirmUpdateVersioningStatus("enabled");
      },
      title: `Are you sure you want to enable versioning for the bucket "${
        selectedItemName ?? "selected"
      }"?`,
      confirmButtonLabel: "Enable"
    },
    [DIALOG_TYPES.SUSPEND_VERSIONING]: {
      onConfirm: () => {
        handleConfirmUpdateVersioningStatus("suspended");
      },
      title: `Are you sure you want to suspend versioning for the bucket "${
        selectedItemName ?? "selected"
      }"?`,
      confirmButtonLabel: "Suspend"
    },
    [DIALOG_TYPES.DELETE]: {
      onConfirm: handleConfirmDeleteBucket,
      title: `Are you sure you want to delete "${
        selectedItemName ?? "selected"
      }" bucket?`,
      fields: [
        {
          name: "nonEmptyWarning",
          type: FIELD_TYPES.NOTES,
          label: "⚠️ Deletion of bucket is only possible if it is empty."
        }
      ],
      confirmButtonLabel: "Delete"
    }
  };

  return (
    <>
      <Table
        isSearchEnabled={true}
        isSortingEnabled={true}
        rows={bucketsList || []}
        columns={tableColumns}
        actions={tableActions}
        itemLink={{
          column: "name",
          getURL: generateTableItemURL
        }}
        isLoading={!bucketsList}
        tabs={tabs}
        toolbarItems={
          <Button
            onClick={handleCreateBucketButtonClick}
            variant={"contained"}
            disabled={false}
          >
            Create Bucket
          </Button>
        }
      />
      <FormDialog
        isOpened={dialog.isOpened}
        onCancel={handleCloseDialog}
        fields={dialogProps[dialog.type].fields}
        onConfirm={dialogProps[dialog.type].onConfirm}
        title={dialogProps[dialog.type].title}
        confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
      />
    </>
  );
};
